import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle, XCircle, DollarSign, Info } from 'lucide-react';
import * as api from '../services/api';

const SubscriptionManagementPage = ({ user, workerApiUrl, onBack, onSubscriptionChange }) => {
  const [plans, setPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isManaging, setIsManaging] = useState(false);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        const fetchedPlans = await api.getSubscriptionPlans(workerApiUrl);
        setPlans(fetchedPlans);
        const fetchedUserSubscription = await api.getUserSubscription(workerApiUrl);
        setUserSubscription(fetchedUserSubscription.subscription);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        setError(err.message || 'Failed to fetch subscription data.');
        setLoading(false);
      }
    };
    fetchSubscriptionData();
  }, [workerApiUrl, onSubscriptionChange]);

  const handleCreateOrUpdateSubscription = async () => {
    if (!selectedPlan) return;

    setIsManaging(true);
    setError(null);

    try {
      if (userSubscription) {
        // Update existing subscription
        await api.updateSubscription(workerApiUrl, { plan_id: selectedPlan.id });
        alert(`Successfully updated to ${selectedPlan.name} plan!`);
      } else {
        // Create new subscription
        await api.createSubscription(workerApiUrl, { plan_id: selectedPlan.id });
        alert(`Successfully subscribed to ${selectedPlan.name} plan!`);
      }
      // Trigger a refresh of user data in App.js to get updated plan
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
      setSelectedPlan(null);
    } catch (err) {
      console.error('Subscription management error:', err);
      setError(err.message || 'Failed to manage subscription.');
    } finally {
      setIsManaging(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!userSubscription) return;

    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setIsManaging(true);
    setError(null);

    try {
      await api.cancelSubscription(workerApiUrl);
      alert('Subscription canceled successfully!');
      if (onSubscriptionChange) {
        onSubscriptionChange();
      }
      setUserSubscription(null); // Clear local state
    } catch (err) {
      console.error('Cancellation error:', err);
      setError(err.message || 'Failed to cancel subscription.');
    } finally {
      setIsManaging(false);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading subscriptions...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  const isCurrentPlan = (plan) => userSubscription?.plan_id === plan.id || (!userSubscription && plan.name === 'Free');

  return (
    <div className="subscription-management-page">
      <div className="subscription-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={18} />
          Back to Profile
        </button>
        <h1>Manage Your Subscription</h1>
      </div>

      {userSubscription && (
        <div className="current-subscription-info settings-card">
          <h2>Your Current Plan: {userSubscription.subscription_plans?.name || 'Free'}</h2>
          <p className="current-plan-status">Status: <span className={`status-${userSubscription.status}`}>{userSubscription.status}</span></p>
          {userSubscription.subscription_plans?.price > 0 && (
            <p>Price: {userSubscription.subscription_plans.currency} {userSubscription.subscription_plans.price.toFixed(2)} / month</p>
          )}
          {userSubscription.end_date && <p>Renews on: {new Date(userSubscription.end_date).toLocaleDateString()}</p>}
          {userSubscription.trial_end && <p>Trial ends: {new Date(userSubscription.trial_end).toLocaleDateString()}</p>}
          
          <div className="current-plan-features">
            <h3>Features:</h3>
            <ul>
              {(userSubscription.subscription_plans?.features || []).map((feature, index) => (
                <li key={index}><CheckCircle size={16} className="feature-icon-check" /> {feature}</li>
              ))}
            </ul>
          </div>

          {userSubscription.status === 'active' && userSubscription.subscription_plans?.name !== 'Free' && (
            <button 
              className="btn-danger mt-4" 
              onClick={handleCancelSubscription} 
              disabled={isManaging}
            >
              {isManaging ? 'Canceling...' : 'Cancel Subscription'}
            </button>
          )}
        </div>
      )}

      <h2 className="all-plans-heading">All Available Plans</h2>
      <div className="subscription-plans-grid">
        {plans.map((plan) => (
          <div key={plan.id} className={`plan-card ${isCurrentPlan(plan) ? 'current-plan' : ''}`}>
            <h3>{plan.name} {isCurrentPlan(plan) && <span className="current-plan-badge">(Current Plan)</span>}</h3>
            <p className="plan-description">{plan.description}</p>
            <div className="plan-price">
              <span className="price-amount">{plan.currency} {plan.price.toFixed(2)}</span>
              <span className="price-period"> / month</span>
            </div>
            <ul className="plan-features">
              {plan.features.map((feature, index) => (
                <li key={index}><CheckCircle size={16} className="feature-icon-check" /> {feature}</li>
              ))}
            </ul>
            <button
              className={`btn-primary ${isCurrentPlan(plan) || isManaging ? 'disabled' : ''}`}
              onClick={() => setSelectedPlan(plan)}
              disabled={isCurrentPlan(plan) || isManaging}
            >
              {isCurrentPlan(plan) ? 'Current Plan' : `Choose ${plan.name} Plan`}
            </button>
            {selectedPlan?.id === plan.id && (
              <div className="plan-confirmation-dialog">
                <p>Confirm changing to {selectedPlan.name} plan?</p>
                <button 
                  className="btn-primary" 
                  onClick={handleCreateOrUpdateSubscription}
                  disabled={isManaging}
                >
                  {isManaging ? 'Confirming...' : 'Confirm'}
                </button>
                <button 
                  className="btn-secondary" 
                  onClick={() => setSelectedPlan(null)}
                  disabled={isManaging}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionManagementPage;
